import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Modal } from 'antd';
import type { TradeRoute } from '../types';

interface TradeRouteMapProps {
  routes: TradeRoute[];
  width?: number;
  height?: number;
  onRouteClick?: (route: TradeRoute) => void;
}

/**
 * 贸易路径地图组件
 * 使用D3.js绘制世界地图，展示贸易路径弧线
 * 
 * 功能：
 * - 世界地图底图
 * - 贸易路径弧线
 * - 弧线粗细映射贸易量
 * - 弧线颜色映射产品类型
 * - 悬停显示详情
 * - 动画效果
 * - 缩放和平移
 * 
 * 验证需求：13.1, 13.3
 * 属性18：贸易路径可视化完整性
 */
const TradeRouteMap: React.FC<TradeRouteMapProps> = ({
  routes,
  width = 1200,
  height = 600,
  onRouteClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedRoute, setSelectedRoute] = useState<TradeRoute | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    if (!svgRef.current) return;

    // 清空SVG
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    // 创建容器组（用于缩放和平移）
    const g = svg.append('g');

    // 地图投影（Mercator投影）
    const projection = d3.geoMercator()
      .scale(180)
      .translate([width / 2, height / 1.5]);

    const path = d3.geoPath().projection(projection);

    // 绘制简单的背景 (Light)
    g.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', '#f0f5ff') // Light blueish white
      .attr('stroke', '#d9d9d9')
      .attr('stroke-width', 1)
      .attr('rx', 8)
      .attr('ry', 8);

    // Load world map data
    d3.json('/data/world.json').then((data: any) => {
      if (!svgRef.current) return;

      const worldData = data;

      // Draw map land (Light Theme)
      g.selectAll('path.land')
        .data(worldData.features)
        .enter()
        .append('path')
        .attr('class', 'land')
        .attr('d', path as any)
        .attr('fill', '#e6e6e6') // Light grey land
        .attr('stroke', '#fff')
        .attr('stroke-width', 0.5);

      drawRoutes();
    });

    const drawRoutes = () => {

      // 计算弧线宽度的比例尺 (unchanged)
      const volumeExtent = d3.extent(routes, d => d.trade_volume_tons) as [number, number];
      const widthScale = d3.scaleSqrt()
        .domain(volumeExtent)
        .range([1, 8]); // 最小1px，最大8px

      // 产品类型颜色映射 (Light Theme Contrast)
      const productColorMap: Record<string, string> = {
        'V2O5': '#f5222d',      // Red
        'FeV50': '#1890ff',     // Blue
        'FeV80': '#52c41a',     // Green
        'VN': '#faad14',        // Orange
      };

      // 1. Separate routes into normal and focus groups
      const normalRoutes = routes.filter(r => !r.is_focus && !r.origin_city.includes('承德'));
      const focusRoutes = routes.filter(r => r.is_focus || r.origin_city.includes('承德'));
      const sortedRoutes = [...normalRoutes, ...focusRoutes];

      // 绘制路径 (使用贝塞尔曲线或大圆弧)
      g.selectAll('path.route')
        .data(sortedRoutes)
        .enter()
        .append('path')
        .attr('class', 'route')
        .attr('d', d => {
          const source = projection([d.origin_lon, d.origin_lat]);
          const target = projection([d.destination_lon, d.destination_lat]);

          if (!source || !target) return null;

          // Draw standard curve
          const dx = target[0] - source[0];
          const dy = target[1] - source[1];
          const dr = Math.sqrt(dx * dx + dy * dy) * 1.5; // Curvature

          return `M${source[0]},${source[1]}A${dr},${dr} 0 0,1 ${target[0]},${target[1]}`;
        })
        .attr('fill', 'none')
        .attr('stroke', d => {
          // Task 5: Gold/Dark Orange color for focus routes
          if (d.is_focus || d.origin_city.includes('承德')) return '#d48806'; // Dark Gold
          return productColorMap[d.product_type] || '#1890ff';
        })
        .attr('stroke-width', d => {
          // Task 5: Thicker stroke for focus routes
          const baseWidth = widthScale(d.trade_volume_tons);
          if (d.is_focus || d.origin_city.includes('承德')) return baseWidth * 2;
          return baseWidth;
        })
        .attr('stroke-opacity', d => {
          if (d.is_focus || d.origin_city.includes('承德')) return 0.9;
          return 0.6;
        })
        .attr('stroke-linecap', 'round')
        .style('cursor', 'pointer')
        // Add glow effect for focus routes
        .style('filter', d => {
          if (d.is_focus || d.origin_city.includes('承德')) return 'drop-shadow(0 0 2px rgba(212, 136, 6, 0.6))';
          return 'none';
        })
        .on('mouseover', function (event, d) {
          d3.select(this).attr('stroke-opacity', 1).attr('stroke-width', (d: any) => widthScale(d.trade_volume_tons) * 2 + 2);

          // Show tooltip (Light Theme)
          const tooltip = d3.select('body').append('div')
            .attr('class', 'trade-route-tooltip')
            .style('position', 'absolute')
            .style('background', 'rgba(255, 255, 255, 0.95)')
            .style('color', '#333')
            .style('padding', '8px 12px')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('z-index', 1000)
            .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
            .style('border', '1px solid #eee')
            .html(`
              <div><strong>${d.origin_city} -> ${d.destination_city}</strong></div>
              <div>${d.product_type}: ${d.trade_volume_tons} tons</div>
              ${d.is_focus ? '<div style="color: #d48806">★ 承德专线</div>' : ''}
            `);

          tooltip.style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
        })
        .on('mouseout', function (event, d) {
          const isFocused = d.is_focus || d.origin_city.includes('承德');
          d3.select(this)
            .attr('stroke-opacity', isFocused ? 0.9 : 0.6)
            .attr('stroke-width', (d: any) => {
              const base = widthScale(d.trade_volume_tons);
              return isFocused ? base * 2 : base;
            });
          d3.select('.trade-route-tooltip').remove();
        })
        .on('click', (event, d) => {
          setSelectedRoute(d);
          setModalVisible(true);
          if (onRouteClick) onRouteClick(d);
        });

      // 绘制起点和终点标记
      const locations = routes.flatMap(r => [
        { lon: r.origin_lon, lat: r.origin_lat, city: r.origin_city, type: 'origin' },
        { lon: r.destination_lon, lat: r.destination_lat, city: r.destination_city, type: 'destination' },
      ]);

      // 去重位置
      const uniqueLocations = Array.from(
        new Map(locations.map(loc => [`${loc.lon},${loc.lat}`, loc])).values()
      );

      // Create location markers
      g.selectAll('circle.location-marker')
        .data(uniqueLocations)
        .enter()
        .append('circle')
        .attr('class', 'location-marker')
        .attr('cx', d => {
          const coords = projection([d.lon, d.lat]);
          return coords ? coords[0] : 0;
        })
        .attr('cy', d => {
          const coords = projection([d.lon, d.lat]);
          return coords ? coords[1] : 0;
        })
        .attr('r', 4)
        .attr('fill', d => d.type === 'origin' ? '#f5222d' : '#1890ff')
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
        .attr('opacity', 0.9);

      // 添加缩放和平移功能
      const zoom = d3.zoom<SVGSVGElement, unknown>()
        .scaleExtent([1, 8])
        .on('zoom', (event) => {
          g.attr('transform', event.transform);
        });

      svg.call(zoom);
    }; // End drawRoutes

    return () => {
      d3.select('.trade-route-tooltip').remove();
    };
  }, [routes, width, height, onRouteClick]);

  // 运输方式映射
  const getTransportModeText = (mode?: string) => {
    const modeMap: Record<string, string> = {
      sea: '海运',
      rail: '铁路',
      road: '公路',
      air: '空运',
    };
    return mode ? modeMap[mode] || mode : '未知';
  };

  return (
    <>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        style={{
          width: '100%',
          height: 'auto',
          border: '1px solid rgba(0,0,0,0.05)',
          borderRadius: '8px',
          background: '#f0f5ff',
          display: 'block'
        }}
      />

      {/* 贸易路径详情Modal (Light) */}
      <Modal
        title={<span style={{ color: '#1f1f1f' }}>贸易路径详情</span>}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        styles={{
          content: { background: '#fff', color: '#1f1f1f' },
          header: { background: 'transparent', borderBottom: '1px solid #f0f0f0' },
          mask: { backdropFilter: 'blur(4px)' }
        }}
        closeIcon={<span style={{ color: '#999' }}>×</span>}
      >
        {selectedRoute && (
          <div style={{ fontSize: '14px' }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ marginBottom: '8px', color: '#1f1f1f' }}>
                {selectedRoute.origin_city} → {selectedRoute.destination_city}
              </h3>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px' }}>
              <div style={{ color: '#666' }}>年份：</div>
              <div>{selectedRoute.year}</div>

              <div style={{ color: '#666' }}>起始地：</div>
              <div>
                {selectedRoute.origin_city}, {selectedRoute.origin_country}
                <br />
                <span style={{ fontSize: '12px', color: '#999' }}>
                  ({selectedRoute.origin_lat.toFixed(4)}°, {selectedRoute.origin_lon.toFixed(4)}°)
                </span>
              </div>

              <div style={{ color: '#666' }}>目的地：</div>
              <div>
                {selectedRoute.destination_city}, {selectedRoute.destination_country}
                <br />
                <span style={{ fontSize: '12px', color: '#999' }}>
                  ({selectedRoute.destination_lat.toFixed(4)}°, {selectedRoute.destination_lon.toFixed(4)}°)
                </span>
              </div>

              <div style={{ color: '#666' }}>产品类型：</div>
              <div><strong>{selectedRoute.product_type}</strong></div>

              <div style={{ color: '#666' }}>贸易量：</div>
              <div>
                <strong>{selectedRoute.trade_volume_tons.toLocaleString()} 吨</strong>
              </div>

              {selectedRoute.trade_value_usd && (
                <>
                  <div style={{ color: '#666' }}>贸易额：</div>
                  <div>${selectedRoute.trade_value_usd.toLocaleString()}</div>
                </>
              )}

              {selectedRoute.transport_mode && (
                <>
                  <div style={{ color: 'rgba(255,255,255,0.6)' }}>运输方式：</div>
                  <div>{getTransportModeText(selectedRoute.transport_mode)}</div>
                </>
              )}

              <div style={{ color: 'rgba(255,255,255,0.6)' }}>数据来源：</div>
              <div>{selectedRoute.data_source}</div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default TradeRouteMap;
